import { Injectable, inject, OnDestroy } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree, CanActivateFn } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, filter, switchMap, takeUntil } from 'rxjs/operators';

import { socialAppSelectors } from 'app/modules/social/state/app';
import { Loggable } from 'app/utils/logging/loggable';
import SocialRoomSet from 'entity/SocialRoomSet';
import EntryOptionType from 'enums/EntryOptionType';

import { AuthService } from '../../services/auth.service';
import { TermsService } from '../../services/terms.service';
import { AuthenticationState, coreSelectors } from '../state';

@Injectable({
  providedIn: 'root',
})
export class SocialGuardService extends Loggable implements OnDestroy {
  private destroyed$ = new ReplaySubject<boolean>(1);
  public constructor(
    public router: Router,
    public authService: AuthService,
    public store: Store,
    public termsService: TermsService,
  ) {
    super();
    this.setDisplayName('SocialGuardService');
    store
      .select(socialAppSelectors.getCurrentConnectedRoomSet)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((connectedRoomSet) => {
        this.connectedRoomSet = connectedRoomSet;
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public connectedRoomSet: SocialRoomSet | undefined = undefined;
  public authState: AuthenticationState | undefined = undefined;

  private navigateToWelcome(state: RouterStateSnapshot) {
    return this.router.createUrlTree(['welcome'], {
      queryParams: {
        return: state.url,
      },
    });
  }

  public navigateToWelcomeSocial(state: RouterStateSnapshot, route: ActivatedRouteSnapshot): UrlTree {
    const dataPassFirstName = route.queryParamMap.get('firstname');
    const queryParamData = dataPassFirstName
      ? {
          firstname: dataPassFirstName,
          lastname: route.queryParamMap.get('lastname'),
          email: route.queryParamMap.get('email'),
        }
      : {};

    return this.router.createUrlTree(['guest'], {
      queryParams: {
        ...queryParamData,
        return: state.url,
        social: route.paramMap.get('id'),
        roomSet: route.paramMap.get('sid'),
        registrationId: route.paramMap.get('registrationId'),
      },
    });
  }

  public navigateToTermsUpdate(state: RouterStateSnapshot) {
    return this.router.createUrlTree(['web/shared/updated-terms'], {
      queryParams: {
        return: state.url,
      },
    });
  }

  public needsRedirectToAcceptTerms(state: RouterStateSnapshot) {
    return this.termsService
      .didAcceptTerms()
      .then((didAcceptLastTerms) => {
        if (!didAcceptLastTerms.valueOf()) {
          return this.navigateToTermsUpdate(state);
        }
        return false;
      })
      .catch((error) => {
        this.error('Error checking user accepted terms', error);
        return this.navigateToWelcome(state);
      });
  }
}

export const canActivateSocial: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean | UrlTree> => {
  const socialGuardService = inject(SocialGuardService);

  const data = {
    isSignedIn: false,
    isGuest: false,
  };

  return socialGuardService.store.select(coreSelectors.getAuthenticationState).pipe(
    filter((authenticationState) => authenticationState !== AuthenticationState.INIT),
    switchMap(async (authState) => {
      if (authState === AuthenticationState.UNAUTHENTICATED) {
        return socialGuardService.navigateToWelcomeSocial(state, route);
      }
      if (socialGuardService.authService.isAnonymous()) {
        data.isGuest = true;
      }

      data.isSignedIn = true;

      //Check if user accepted the terms
      if (!data.isGuest) {
        const acceptedTermsReturn = await socialGuardService.needsRedirectToAcceptTerms(state);
        if (acceptedTermsReturn) {
          return acceptedTermsReturn;
        }
      }

      // we should only perform this check if entry configuration is set to starting point
      if (socialGuardService.connectedRoomSet?.entryOption !== EntryOptionType.STARTING_POINT) {
        return true;
      }

      // EntryOptionType.STARTING_POINT, !startingPointSocialId
      if (!socialGuardService.connectedRoomSet?.startingPointSocialId) {
        return true;
      }
      // we have a starting point...
      if (route.params.sid && socialGuardService.connectedRoomSet?.startingPointSocialId) {
        // ...and it does not match the current social id
        if (socialGuardService.connectedRoomSet?.startingPointSocialId !== route.params.id) {
          return socialGuardService.router.parseUrl(
            '/connected-room/' + route.params.sid + '/' + socialGuardService.connectedRoomSet?.startingPointSocialId,
          );
          // ...and it matches the current social id
        } else if (socialGuardService.connectedRoomSet?.startingPointSocialId === route.params.id) {
          return true;
          // ...and something has gone wrong
        } else {
          socialGuardService.error('Error in canActivate: something bad happened');
          return false;
        }
      } else if (socialGuardService.connectedRoomSet?.startingPointSocialId === null) {
        // no starting point set
        return true;
      }
      // we should never get here
      return true;
    }),
    catchError((error: unknown): Observable<boolean> => {
      socialGuardService.error('Error in canActivate', error);
      socialGuardService.navigateToWelcomeSocial(state, route);
      return of(false);
    }),
  );
};
